/**
 * Section → capability requirements (ADM-TKT-0103 / ADM-GAP-045).
 * Single SoT for SectionAccessGate, navCapability, and deep-link AuthZ.
 * Dashboard spreads resolveSectionCapabilityRequirement(section) into SAG — no inline caps.
 */
import { TENANT_RECONCILIATION_READ } from 'pi-kiosk-shared';
import type { DashboardSection } from '../../dashboard/components/Dashboard/DashboardView.js';

export interface SectionCapabilityRequirement {
  readonly capability?: string;
  readonly anyOf?: readonly string[];
  readonly allOf?: readonly string[];
}

/**
 * Capability requirements per dashboard section (SAG + map SoT).
 * Users/Team shell (Package D / Spec §8): section entry via adminUsers only —
 * advanced governance caps (`tenant.adminUserCapabilities.*`, catalog) stay page-local
 * behind permission_management_rbac; do not require them for section entry.
 */
export const USERS_CAPS_SECTION_CAPABILITIES = [
  'tenant.adminUsers.view',
  'tenant.adminUsers.manage',
] as const;

export const SECTION_CAPABILITY_MAP: Readonly<
  Partial<Record<DashboardSection, SectionCapabilityRequirement>>
> = {
  // Package D: basic Team shell via adminUsers; Growth RBAC panels gated inside the page.
  capabilities: { anyOf: USERS_CAPS_SECTION_CAPABILITIES },
  fulfillment: {
    anyOf: ['tenant.orders.fulfill.read', 'tenant.orders.fulfill.update'],
  },
  orders: { capability: 'tenant.orders.complete' },
  'donation-projects': {
    anyOf: ['ops:donation-projects:read', 'ops:donation-projects:manage'],
  },
  'pickup-points': {
    anyOf: ['tenant.pickupPoints.read', 'tenant.pickupPoints.manage'],
  },
  'shipping-methods': {
    anyOf: ['tenant.shippingMethods.read', 'tenant.shippingMethods.manage'],
  },
  paymentsHub: { capability: TENANT_RECONCILIATION_READ },
  'accounting-export': {
    anyOf: ['ops:accounting-export:create', 'ops:accounting-export:read'],
  },
  'loyalty-program': {
    anyOf: [
      'loyalty:coupons:read',
      'loyalty:enrollment:manage',
      'loyalty:campaigns:read',
      'loyalty:config:read',
      'loyalty:config:manage',
    ],
  },
  'promotions-program': {
    capability: 'promo:events:manage',
  },
  // Package C FREE shell — analytics_basic + basic:read (PRO tabs gate overview+summary inside).
  analytics: { capability: 'analytics:basic:read' },
  reports: { capability: 'analytics:basic:read' },
  'mission-control': {
    capability: 'analytics:mission-control:read',
  },
  // Explore: analytics_explore block + detailed+pii caps (RBAC within explore).
  'customer-explore': {
    allOf: ['analytics:detailed:read', 'analytics:pii:read'],
  },
  transactions: { capability: 'ops:transactions:read' },
  // Canonical SoT (pages + SAG + nav). Legacy system:logs:read / system:pii:read
  // expand → these via capabilityBridgeRules forward bridge.
  'audit-logs': { capability: 'tenant.systemLogs.view' },
  'consent-management': { capability: 'tenant.systemPii.view' },
  'tax-management': { capability: 'config:pricing:read' },
  inventory: { capability: 'ops:inventory:read' },
  'inventory-incidents': { capability: 'ops:inventory:read' },
  products: { capability: 'ops:products:read' },
  kiosks: { capability: 'ops:sales-points:read' },
  /**
   * account-settings: entitlement-only (`tenant_ops_settings`) — intentional (G22).
   * No capability gate; any authenticated tenant admin in scope may open settings when block visible.
   *
   * bank-accounts: CORE entitlement ∧ tenant.bankAccounts.read (nav ≡ SAG ≡ list GET).
   * Not-given ∧ not-shown — never entitlement-visible then list 403.
   */
  'bank-accounts': { capability: 'tenant.bankAccounts.read' },
};

export function resolveSectionCapabilityRequirement(
  section: DashboardSection,
): SectionCapabilityRequirement {
  return SECTION_CAPABILITY_MAP[section] ?? {};
}

export function sectionHasCapabilityRequirement(section: DashboardSection): boolean {
  const req = resolveSectionCapabilityRequirement(section);
  return (
    req.capability !== undefined ||
    (req.anyOf !== undefined && req.anyOf.length > 0) ||
    (req.allOf !== undefined && req.allOf.length > 0)
  );
}
